import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import { DebounceField } from '@openx/components-core/lib/DebounceField/DebounceField';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails';
import { SegmentPartnerRevenueMethod } from '@openx/types/gqlTypes';
import { type ChangeEvent, useCallback, useMemo } from 'react';
import { formatNetFee } from './utils/formatters';
import { getPlatformFeeLabel } from './utils/labels';
import { calculateNetFee } from './utils/pricingCalculations';

export type PlatformFeeInputProps = {
  pricingType: SegmentPartnerRevenueMethod;
  maxPrice: number;
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  helperText?: string | null;
};

export const PlatformFeeInput = ({
  pricingType,
  maxPrice,
  value,
  onChange,
  error = null,
  helperText = null,
}: PlatformFeeInputProps) => {
  const isCpm = pricingType === SegmentPartnerRevenueMethod.Cpm;
  const netFee = useMemo(() => calculateNetFee(value, pricingType, maxPrice), [value, pricingType, maxPrice]);
  const resolvedHelperText = error ?? helperText ?? 'Fee will be reduced by OX fee.';
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange]
  );

  return (
    <Box
      sx={{
        mt: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
          }}
        >
          <Typography sx={{ color: 'text.secondary', lineHeight: '16px', mb: 0.75, fontSize: '12px', ml: 1.5 }}>
            {getPlatformFeeLabel(pricingType)}
          </Typography>

          <DebounceField
            fullWidth
            value={value}
            onChange={handleChange}
            error={!!error}
            helperText={resolvedHelperText}
            placeholder="0.0"
            size="small"
            variant="outlined"
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            ml: 1,
          }}
        >
          <FieldDetails title="Net Fee" value={formatNetFee(netFee, isCpm)} />
        </Box>
      </Box>
    </Box>
  );
};
